import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pingTask } from '../../../util/apiService';
import { 
  closeDelete, 
  setDeleteOriginalData,
  setDeleteNewData,
  setPreviewMode, 
  setComment,
  setLoading
} from '../../../redux/previewSlice';
import { getRevAllNodes, getProcedureRevisionById } from '../../../redux/procedureRevisionSlice';
import { deleteElement } from '../../../redux/sectionSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { generateSHA256 } from '../../../util/generateSHA256';
import { getAuditTrailData, extractVersionStructure } from '../../../util/getAuditTrailData';
import VersionHistoryCopyAppBar from '../../../common/VersionHistoryCopyAppBar';
import DeleteReplacePreview from './components/DeleteReplacePreview';
import Loader from '../../../common/Loader';
import { getRevAndTraining, uploadJsonToS3, prepareCurrentSnapshot } from '../../../util/AppUtils';

/**
 * DeleteReplace Component
 * Main component for delete functionality
 * Manages the flow: Preview → Confirm → Delete
 */
const DeleteReplace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    deleteDocument,
    deleteNode,
    deleteOriginalData,
    deleteNewData,
    previewMode, 
    comment,
    isLoading
  } = useSelector(state => state.preview);

  const authUser = useSelector((state) => state.auth.user);
  
  const [step, setStep] = useState('preview'); // 'preview' | 'confirm'
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isFirstConfirmModalOpen, setIsFirstConfirmModalOpen] = useState(false);
  const [procedureType, setProcedureType] = useState(null);
  const [revisionStatus, setRevisionStatus] = useState('Not Submitted');
  
  // Start ping interval for delete document
  useEffect(() => {
    if (!deleteDocument?.revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: deleteDocument.revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for delete document in DeleteReplace:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [deleteDocument?.revisionId, authUser]);

  // Compute new data structure after deleting the node
  const computeNewDataAfterDelete = useCallback((originalData, nodeToDelete) => {
    if (!nodeToDelete || !originalData) return originalData;
    
    // Use structuredClone for proper deep cloning
    const newData = structuredClone ? structuredClone(originalData) : JSON.parse(JSON.stringify(originalData));
    
    try {
      if (nodeToDelete.nodeType === 'section') {
        // Remove section from document
        const filtered = newData.filter(
          section => section.tree_node_id !== nodeToDelete.nodeId
        );
        
        // Reassign sibling orders
        filtered.forEach((section, index) => {
          section.sibling_order = index.toString();
        });
        
        return filtered;
      } else if (nodeToDelete.nodeType === 'step') {
        // Remove step from its section
        for (const section of newData) {
          if (section.tree_node_id === nodeToDelete.sectionId) {
            if (section.steps) {
              const filteredSteps = section.steps.filter(
                step => step.tree_node_id !== nodeToDelete.nodeId
              );
              
              // Reassign sibling orders
              filteredSteps.forEach((step, index) => {
                step.sibling_order = index.toString();
              });
              
              section.steps = filteredSteps;
            }
            break;
          }
        }
        return newData;
      } else if (nodeToDelete.nodeType === 'substep') {
        // Remove substep from its step
        for (const section of newData) {
          if (section.tree_node_id === nodeToDelete.sectionId) {
            for (const step of (section.steps || [])) {
              if (step.tree_node_id === nodeToDelete.stepId) {
                if (step.substeps) {
                  const filteredSubsteps = step.substeps.filter(
                    substep => substep.tree_node_id !== nodeToDelete.nodeId
                  );
                  
                  // Reassign sibling orders
                  filteredSubsteps.forEach((substep, index) => {
                    substep.sibling_order = index.toString();
                  });
                  
                  step.substeps = filteredSubsteps;
                }
                break;
              }
            }
            break;
          }
        }
        return newData;
      } else if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(nodeToDelete.nodeType)) {
        // Remove child node from its parent (step or substep)
        // Find the parent step and substep first
        const section = newData.find(sec => sec.tree_node_id === nodeToDelete.sectionId);
        if (!section) return newData;
        
        const step = (section.steps || []).find(st => st.tree_node_id === nodeToDelete.stepId);
        if (!step) return newData;
        
        // Determine if child is in step or substep
        let parent = step;
        if (nodeToDelete.substepId) {
          const substep = (step.substeps || []).find(s => s.tree_node_id === nodeToDelete.substepId);
          if (substep) {
            parent = substep;
          }
        }
        
        // Try to find and remove child from the expected parent
        const children = parent.children || [];
        const filtered = children.filter(child => {
          const nodeId = child.tree_node_id;
          const canvasNodeId = child.detail?.canvas_tree_node_id;
          return nodeId !== nodeToDelete.nodeId && canvasNodeId !== nodeToDelete.nodeId;
        });
        
        // Reassign sibling orders
        filtered.forEach((child, index) => {
          child.sibling_order = index.toString();
        });
        
        // Update parent.children directly (parent is already a reference to the correct object)
        parent.children = filtered;
        
        // Debug: Verify the deletion worked
        
        return newData;
      }
      
      return newData;
    } catch (err) {
      console.error('Error computing new data:', err);
      return originalData;
    }
  }, []);

  // Fetch original data for the current document
  useEffect(() => {
    const fetchOriginalData = async () => {
      if (!deleteDocument) return;
      
      
      try {
        setError(null);
        dispatch(setLoading(true));
        
        // Fetch revision data to get the procedure type and status
        const revisionResponse = await dispatch(getProcedureRevisionById(deleteDocument.revisionId)).unwrap();
        const revData = revisionResponse?.results || revisionResponse;
        const procedureTypeName = revData?.part_data?.type?.name || deleteDocument.type;
        setProcedureType(procedureTypeName);
        
        // Extract status from status_changes
        const latestStatus = revData?.status_changes?.find(sc => sc.is_latest)?.status_changed_to;
        const currentStatus = latestStatus || 'Not Submitted';
        setRevisionStatus(currentStatus);
        
        // Fetch original data for the current document
        const originalResponse = await dispatch(getRevAllNodes({ 
          partId: deleteDocument.partId, 
          revisionId: deleteDocument.revisionId 
        })).unwrap();
        
        dispatch(setDeleteOriginalData(originalResponse));
        
        // Compute new data by removing the node
        const newResponse = computeNewDataAfterDelete(originalResponse, deleteNode);
        
        // Debug: Log what we're about to set
        
        dispatch(setDeleteNewData(newResponse));
        
      } catch (err) {
        console.error('Error fetching document data:', err);
        setError('Failed to load document data. Please try again.');
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchOriginalData();
  }, [deleteDocument, deleteNode, dispatch, computeNewDataAfterDelete]);

  // Handle preview mode toggle
  const handlePreviewModeChange = (mode) => {
    dispatch(setPreviewMode(mode));
  };

  // Handle comment change
  const handleCommentChange = (newComment) => {
    dispatch(setComment(newComment));
  };

  // Handle back navigation
  const handleBack = () => {
    if (step === 'confirm') {
      setStep('preview');
    }
  };

  // Handle close
  const handleClose = () => {
    dispatch(closeDelete());
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!comment || !comment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }

    if (!deleteDocument || !deleteNode) {
      dispatch(showSnackbar({ message: 'Delete node information is missing.', type: 'error' }));
      return;
    }

    dispatch(setLoading(true));

    try {
      let fromType, fromTreeId, fromOrder, fromSectionId, fromStepId, fromSubstepId, fromChildNodeRef, fromLtreePath;
      
      if (deleteNode.nodeType === 'section') {
        const section = deleteOriginalData.find(s => s.tree_node_id === deleteNode.nodeId);
        if (!section) {
          dispatch(showSnackbar({ message: 'Section not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        
        fromType = 'section';
        fromTreeId = section.tree_node_id;
        fromOrder = parseInt(section.sibling_order || section.procedure_tree?.sibling_order || '0', 10);
        fromSectionId = section.section_id_ref;
        fromStepId = '';
        fromSubstepId = '';
        fromLtreePath = section.ltree_path || deleteNode.ltree_path || '';
      } else if (deleteNode.nodeType === 'step') {
        const section = deleteOriginalData.find(s => s.tree_node_id === deleteNode.sectionId);
        if (!section) {
          dispatch(showSnackbar({ message: 'Section not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        const step = section.steps?.find(s => s.tree_node_id === deleteNode.nodeId);
        if (!step) {
          dispatch(showSnackbar({ message: 'Step not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        
        fromType = 'step';
        fromTreeId = step.tree_node_id;
        fromOrder = parseInt(step.sibling_order || step.procedure_tree?.sibling_order || '0', 10);
        fromSectionId = section.section_id_ref;
        fromStepId = step.step_id_ref || step.tree_node_id;
        fromSubstepId = '';
        fromLtreePath = step.ltree_path || deleteNode.ltree_path || '';
      } else if (deleteNode.nodeType === 'substep') {
        const section = deleteOriginalData.find(s => s.tree_node_id === deleteNode.sectionId);
        if (!section) {
          dispatch(showSnackbar({ message: 'Section not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        const step = section.steps?.find(s => s.tree_node_id === deleteNode.stepId);
        if (!step) {
          dispatch(showSnackbar({ message: 'Step not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        const substep = step.substeps?.find(s => s.tree_node_id === deleteNode.nodeId);
        if (!substep) {
          dispatch(showSnackbar({ message: 'Substep not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        
        fromType = 'substep';
        fromTreeId = substep.tree_node_id;
        fromOrder = parseInt(substep.sibling_order || substep.procedure_tree?.sibling_order || '0', 10);
        fromSectionId = section.section_id_ref;
        fromStepId = step.step_id_ref || step.tree_node_id;
        fromSubstepId = substep.substep_id_ref || substep.tree_node_id;
        fromLtreePath = substep.ltree_path || deleteNode.ltree_path || '';
      } else if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(deleteNode.nodeType)) {
        const section = deleteOriginalData.find(s => s.tree_node_id === deleteNode.sectionId);
        if (!section) {
          dispatch(showSnackbar({ message: 'Section not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        const step = section.steps?.find(s => s.tree_node_id === deleteNode.stepId);
        if (!step) {
          dispatch(showSnackbar({ message: 'Step not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        
        let child = null;
        let parent = step;
        let foundSubstep = null;
        
        // Find child in step
        if (step.children) {
          child = step.children.find(c => {
            const nodeId = c.tree_node_id;
            const canvasNodeId = c.detail?.canvas_tree_node_id;
            return nodeId === deleteNode.nodeId || canvasNodeId === deleteNode.nodeId;
          });
        }
        
        // If not in step, find in substep
        if (!child && deleteNode.substepId) {
          foundSubstep = step.substeps?.find(s => s.tree_node_id === deleteNode.substepId);
          if (foundSubstep && foundSubstep.children) {
            child = foundSubstep.children.find(c => {
              const nodeId = c.tree_node_id;
              const canvasNodeId = c.detail?.canvas_tree_node_id;
              return nodeId === deleteNode.nodeId || canvasNodeId === deleteNode.nodeId;
            });
            if (child) parent = foundSubstep;
          }
        }
        
        if (!child) {
          dispatch(showSnackbar({ message: 'Child node not found.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        
        fromType = deleteNode.nodeType === 'canva' ? 'canvas' : deleteNode.nodeType;
        fromTreeId = child.tree_node_id;
        fromOrder = parseInt(child.sibling_order || child.procedure_tree?.sibling_order || '0', 10);
        fromSectionId = section.section_id_ref;
        fromStepId = step.step_id_ref || step.tree_node_id;
        fromSubstepId = foundSubstep ? (foundSubstep.substep_id_ref || foundSubstep.tree_node_id) : '';
        // Use detail-specific ID for child_node_ref based on node type
        if (child.type === "attachment") {
          fromChildNodeRef = child?.detail?.attachment_tree_node_id || child.tree_node_id;
        } else if (child.type === "video") {
          fromChildNodeRef = child?.detail?.video_tree_node_id || child.tree_node_id;
        } else if (child.type === "canvas" || child.type === "canva") {
          fromChildNodeRef = child?.detail?.canvas_tree_node_id || child.tree_node_id;
        } else if (child.type === "rich-text") {
          fromChildNodeRef = child?.detail?.rich_text_node_id || child.tree_node_id;
        } else {
          fromChildNodeRef = child.tree_node_id;
        }
        fromLtreePath = child.ltree_path || deleteNode.ltree_path || '';
      }
      
      // Determine action type and action to show based on node type
      const actionType = deleteNode.nodeType === 'section' ? "SECTION_NODE_DELETED" :
                        deleteNode.nodeType === 'step' ? "STEP_NODE_DELETED" :
                        deleteNode.nodeType === 'substep' ? "SUBSTEP_NODE_DELETED" :
                        `${deleteNode?.nodeType?.toUpperCase()}_NODE_DELETED`;
      
      // Calculate section, step, and substep identifiers for action_to_show
      const getOrderValue = (node) => parseInt(node?.sibling_order || node?.procedure_tree?.sibling_order || '0', 10);
      const idsMatch = (value, candidate) => {
        if (value === undefined || value === null || candidate === undefined || candidate === null) {
          return false;
        }
        return String(value) === String(candidate);
      };

      let sectionNumber = 0;
      let stepNumber = 0;
      let sectionTitle = '';
      let stepTitle = '';
      let substepTitle = '';
      let substepLabel = '';

      const sortedSections = Array.isArray(deleteOriginalData)
        ? [...deleteOriginalData].sort((a, b) => getOrderValue(a) - getOrderValue(b))
        : [];

      if (deleteNode.nodeType === 'section') {
        const targetSection = sortedSections.find(
          (section) =>
            idsMatch(section.tree_node_id, deleteNode.nodeId) ||
            idsMatch(section.section_id_ref, deleteNode.nodeId)
        );

        if (targetSection) {
          const sectionIndex = sortedSections.findIndex(
            (section) =>
              idsMatch(section.tree_node_id, targetSection.tree_node_id) ||
              idsMatch(section.section_id_ref, targetSection.section_id_ref)
          );

          sectionNumber = sectionIndex + 1;
          sectionTitle = targetSection.title || `Section ${sectionNumber}`;
        }
      } else {
        const targetSection = sortedSections.find(
          (section) =>
            idsMatch(section.tree_node_id, deleteNode.sectionId) ||
            idsMatch(section.section_id_ref, deleteNode.sectionId)
        );

        if (targetSection) {
          const sectionIndex = sortedSections.findIndex(
            (section) =>
              idsMatch(section.tree_node_id, targetSection.tree_node_id) ||
              idsMatch(section.section_id_ref, targetSection.section_id_ref)
          );

          sectionNumber = sectionIndex + 1;
          sectionTitle = targetSection.title || `Section ${sectionNumber}`;

          const sortedSteps = [...(targetSection.steps || [])].sort(
            (a, b) => getOrderValue(a) - getOrderValue(b)
          );

          const targetStepIndex = sortedSteps.findIndex(
            (step) =>
              idsMatch(step.tree_node_id, deleteNode.stepId) ||
              idsMatch(step.step_id_ref, deleteNode.stepId)
          );

          if (targetStepIndex !== -1) {
            const stepsBeforeSection =
              sectionIndex > 0
                ? sortedSections
                    .slice(0, sectionIndex)
                    .reduce((total, section) => total + (section.steps?.length || 0), 0)
                : 0;

            stepNumber = stepsBeforeSection + targetStepIndex + 1;

            const targetStep = sortedSteps[targetStepIndex];
            if (targetStep) {
              const resolvedTitle = targetStep.title?.trim();
              stepTitle = resolvedTitle ? resolvedTitle : `Step ${stepNumber}`;

              const targetSubstepId = deleteNode.substepId || deleteNode.nodeId;
              if (targetSubstepId) {
                const sortedSubsteps = [...(targetStep.substeps || [])].sort(
                  (a, b) => getOrderValue(a) - getOrderValue(b)
                );
                const targetSubstepIndex = sortedSubsteps.findIndex(
                  (substep) =>
                    idsMatch(substep.tree_node_id, targetSubstepId) ||
                    idsMatch(substep.substep_id_ref, targetSubstepId)
                );

                if (targetSubstepIndex !== -1) {
                  const targetSubstep = sortedSubsteps[targetSubstepIndex];
                  const resolvedSubstepTitle = targetSubstep?.title?.trim();
                  const substepLetter = String.fromCharCode(65 + targetSubstepIndex);
                  substepLabel = `Substep ${stepNumber}${substepLetter}`;
                  substepTitle = resolvedSubstepTitle ? resolvedSubstepTitle : substepLabel;
                }
              }
            }
          }
        }
      }
      
      // Build action_to_show based on node type (using numbered format like "Section X > Step Y")
      let actionToShow = '';
      if (deleteNode.nodeType === 'section') {
        const sectionDisplay = deleteNode.nodeTitle?.trim() || sectionTitle || 'Section';
        actionToShow = `Deleted ${sectionDisplay} in ${deleteDocument.partNumber}, Rev ${deleteDocument.revision}`;
      } else if (deleteNode.nodeType === 'step') {
        const baseStepDisplay =
          deleteNode.nodeTitle?.trim() ||
          stepTitle ||
          (stepNumber ? `Step ${stepNumber}` : 'Step');
        const globalStepLabel = stepNumber ? `Step ${stepNumber}` : '';
        const stepDisplay =
          globalStepLabel && !baseStepDisplay.includes(globalStepLabel)
            ? `${baseStepDisplay} (${globalStepLabel})`
            : baseStepDisplay;
        const sectionSegment = sectionNumber ? `Section ${sectionNumber}` : 'Section';
        actionToShow = `Deleted ${stepDisplay} from ${sectionSegment} in ${deleteDocument.partNumber}, Rev ${deleteDocument.revision}`;
      } else if (deleteNode.nodeType === 'substep') {
        const baseSubstepDisplay =
          deleteNode.nodeTitle?.trim() ||
          substepTitle ||
          substepLabel ||
          'Substep';
        const substepDisplay =
          substepLabel && !baseSubstepDisplay.includes(substepLabel)
            ? `${baseSubstepDisplay} (${substepLabel})`
            : baseSubstepDisplay;
        const locationParts = [];
        locationParts.push(sectionNumber ? `Section ${sectionNumber}` : 'Section');
        if (stepNumber) {
          locationParts.push(`Step ${stepNumber}`);
        }
        const locationPath = locationParts.join(' > ');
        actionToShow = `Deleted ${substepDisplay} from ${locationPath} in ${deleteDocument.partNumber}, Rev ${deleteDocument.revision}`;
      } else {
        // Child nodes (attachment, video, rich-text, canvas, form)
        // Format: "Deleted Video 3 from Section 3 > Step 2 in LKHGHJK, Rev C-"
        const pathParts = [];
        pathParts.push(sectionNumber ? `Section ${sectionNumber}` : 'Section');
        if (stepNumber) {
          pathParts.push(`Step ${stepNumber}`);
        }
        if (substepLabel) {
          pathParts.push(substepLabel);
        }
        const path = pathParts.join(' > ');
        const childDisplay = deleteNode.nodeTitle?.trim() || 'Item';
        actionToShow = `Deleted ${childDisplay} from ${path} in ${deleteDocument.partNumber}, Rev ${deleteDocument.revision}`;
      }
      
    

      // Build audit trail data in old format (matching copy flow)
      

      
      // Generate audit trail data using getAuditTrailData
      const audit_trail_data_from_helper = await getAuditTrailData(
        {
          action: actionType,
          comment: comment,
          version_content: {
            action_to_show: actionToShow,
          },
        },
        true 
      );
      
      const previous_content_snapshot = await uploadJsonToS3(deleteOriginalData,deleteDocument.partNumber,deleteDocument.revision+"_delete_previous",true)

      const revAndTrainingData = await getRevAndTraining(dispatch, deleteDocument.partId)

      const current_snapshot = await uploadJsonToS3(
        prepareCurrentSnapshot({
          allNodesData: deleteNewData,
          part_number: deleteDocument.partNumber,
          procedure_rev: deleteDocument.revision,
          procedure_status: revisionStatus || 'Not Submitted',
          item_code_type: procedureType || deleteDocument.type,
          partDescription: deleteDocument?.description || "",
          part_ref: deleteDocument.partId,
          procedure_rev_ref: deleteDocument.revisionId
        }),
        deleteDocument.partNumber,
        deleteDocument.revision
      );
      const current_updated_snapshot = await uploadJsonToS3(
        prepareCurrentSnapshot({
          allNodesData: deleteNewData,
          part_number: deleteDocument.partNumber,
          procedure_rev: deleteDocument.revision,
          procedure_status: revisionStatus || 'Not Submitted',
          item_code_type: procedureType || deleteDocument.type,
          partDescription: deleteDocument?.description || "",
          part_ref: deleteDocument.partId,
          procedure_rev_ref: deleteDocument.revisionId
        }),
        deleteDocument.partNumber,
        deleteDocument.revision+"_current_updated_snapshot",true
      );
      const new_content_snapshot = await uploadJsonToS3(
        {
          data: deleteNewData,
          action_to_show: actionToShow,
          item_code_type: procedureType || deleteDocument.type,
          partNumber: deleteDocument.partNumber,
          status: revisionStatus || 'Not Submitted',
          revision: deleteDocument.revision,
          operation: "single_audit_trail",
          tree_node_id: fromTreeId,
          is_delete_entry:true,
          partDescription:deleteDocument?.description,
          ...revAndTrainingData,
        },
        deleteDocument.partNumber,
        deleteDocument.revision+"_delete_new",
        true
      );

      // Build payload in the correct format for delete API
      const payload = {
        part_id_ref: deleteDocument.partId,
        procedure_rev_ref: deleteDocument.revisionId,
        section_id_ref: fromSectionId,
        step_id_ref: fromStepId || "",
        substep_id_ref: (fromSubstepId === "null" || fromSubstepId === null) ? "" : fromSubstepId || "",
        ...(fromChildNodeRef ? { child_node_ref: fromChildNodeRef } : {}),
        ltree_path: fromLtreePath || '',
        audit_trail_data: {
          ...audit_trail_data_from_helper,
          previous_content_snapshot,
          new_content_snapshot,
          version_content:{
            action_to_show:actionToShow,
            preview_tree_node_id:fromTreeId
          }
        },
        current_snapshot,
        current_updated_snapshot
        
      };
      
      // Clean up empty or null values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "null" || payload[key] === null) {
          delete payload[key];
        }
      });
      
      

      // Make API call to delete element
      await dispatch(deleteElement(payload)).unwrap();
      
      // Close modal and delete flow
      setModalOpen(false);
      handleClose();
      
      // Show success message
      const successMessage = deleteNode.nodeType === 'step' 
        ? 'Step deleted successfully!' 
        : deleteNode.nodeType === 'substep' 
        ? 'Substep deleted successfully!'
        : deleteNode.nodeType === 'rich-text'
        ? 'Rich Text deleted successfully!'
        : ['attachment', 'video', 'form', 'canva', 'canvas'].includes(deleteNode.nodeType)
        ? `${deleteNode.nodeTitle?.split(' ')[0] || 'Element'} deleted successfully!`
        : 'Section deleted successfully!';
      dispatch(showSnackbar({ message: successMessage, type: 'success' }));
      
      // Navigate to revision-submission page
      const description = deleteDocument.description || 'no-description';
      const encodedDescription = encodeURIComponent(description);
      navigate(
        `/revision-submission/${deleteDocument.partId}/${deleteDocument.revision}/${deleteDocument.type}/${deleteDocument.partNumber}/${deleteDocument.revisionId}/${encodedDescription}`,
        { state: { status: 'Not Submitted' } }
      );
    } catch (error) {
      console.error('Error deleting node:', error);
      const errorMessage = deleteNode.nodeType === 'step' 
        ? 'Error deleting step: ' + (error?.message || 'Unknown error')
        : deleteNode.nodeType === 'substep'
        ? 'Error deleting substep: ' + (error?.message || 'Unknown error')
        : 'Error deleting section: ' + (error?.message || 'Unknown error');
      dispatch(showSnackbar({ message: errorMessage, type: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Show loader during initial data fetch
  if (isLoading && !deleteOriginalData) {
    return <Loader text="Loading document data..." />;
  }

  // Get step title and buttons based on current step
  const getStepConfig = () => {
    switch (step) {
      case 'preview':
        return {
          title: `Please Confirm the Changes
`,
          subheading: '',
          showCancelButton: true,
          onCancel: handleClose,
          showViewToggle: true,
          selectedView: previewMode,
          onViewChange: (mode) => handlePreviewModeChange(mode),
          buttonsBeforeTitle: [],
          buttons: [
            { label: 'Confirm', onClick: () => setIsFirstConfirmModalOpen(true) }
          ]
        };
      default:
        return {
          title: 'Delete',
          subheading: '',
          buttonsBeforeTitle: [],
          buttons: []
        };
    }
  };

  const stepConfig = getStepConfig();

  return (
    <div>
      <VersionHistoryCopyAppBar 
        title={stepConfig.title}
        subheading={stepConfig.subheading}
        buttons={stepConfig.buttons}
        buttonsBeforeTitle={stepConfig.buttonsBeforeTitle || []}
        showBackButton={false}
        showProfileButton={false}
        showTablesDropdown={false}
        showCancelButton={stepConfig.showCancelButton || false}
        onCancel={stepConfig.onCancel}
        showViewToggle={stepConfig.showViewToggle || false}
        selectedView={stepConfig.selectedView}
        onViewChange={stepConfig.onViewChange}
      />
      
      {/* Preview */}
      {step === 'preview' && deleteDocument && (
        <DeleteReplacePreview
          deleteDocument={deleteDocument}
          deleteNode={deleteNode}
          previewMode={previewMode}
          originalData={deleteOriginalData}
          newData={deleteNewData}
          onPreviewModeChange={handlePreviewModeChange}
          onConfirm={handleConfirmDelete}
          comment={comment}
          onCommentChange={handleCommentChange}
          isLoading={isLoading}
          isModalOpen={isModalOpen}
          onModalClose={() => setModalOpen(false)}
          onOpenSaveModal={() => setModalOpen(true)}
          isFirstConfirmModalOpen={isFirstConfirmModalOpen}
          onFirstConfirmModalClose={() => setIsFirstConfirmModalOpen(false)}
          procedureType={procedureType || deleteDocument.type}
          revisionStatus={revisionStatus}
        />
      )}
    </div>
  );
};

export default DeleteReplace;

